/**
 * Dashboard OCR Helper
 * 
 * Client-side OCR for dash photos using Tesseract.js
 * Parses odometer readings and warning text
 */

import Tesseract from 'tesseract.js';

export interface DashboardOcrResult {
  /** Full OCR text extracted from image */
  raw_text: string;
  /** Parsed odometer candidates (numbers that look like mileage) */
  odometer_candidates: number[];
  /** Best guess for odometer reading */
  selected_odometer?: number;
  /** Text-based warning keywords detected */
  warnings_detected: string[];
  /** Processing time in ms */
  processing_time_ms: number;
}

// Common odometer patterns: 123456, 123,456, 123456 mi, etc.
const ODOMETER_PATTERNS = [
  /(\d{1,3}(?:,\d{3})+)\s*(?:mi(?:les)?)?/gi,  // With commas: 123,456 mi
  /(\d{4,7})\s*(?:mi(?:les)?)?/gi,              // Without commas: 123456 mi
];

// Warning keywords to detect (text-based only, no CV)
const WARNING_KEYWORDS = [
  'CHECK ENGINE',
  'SERVICE ENGINE SOON',
  'LOW OIL',
  'OIL PRESSURE',
  'LOW FUEL',
  'LOW TIRE',
  'TPMS',
  'ABS',
  'AIRBAG',
  'SRS',
  'BRAKE',
  'BATTERY',
  'TEMP',
  'OVERHEAT',
  'COOLANT',
  'MAINTENANCE',
  'SERVICE REQUIRED',
  'CHANGE OIL',
];

/**
 * Extract odometer candidates from OCR text
 */
function parseOdometerCandidates(text: string): number[] {
  const candidates: number[] = [];
  const seen = new Set<number>();

  for (const pattern of ODOMETER_PATTERNS) {
    const matches = text.matchAll(pattern);
    for (const match of matches) {
      // Remove commas and parse as number
      const numStr = match[1].replace(/,/g, '');
      const num = parseInt(numStr, 10);
      
      // Filter reasonable mileage range (100 to 999999)
      if (num >= 100 && num <= 999999 && !seen.has(num)) {
        candidates.push(num);
        seen.add(num);
      }
    }
  }

  // Sort by likelihood (prefer larger numbers as they're more likely to be odometer)
  return candidates.sort((a, b) => b - a);
}

/**
 * Detect warning text in OCR output
 */
function detectWarnings(text: string): string[] {
  const upperText = text.toUpperCase();
  const detected: string[] = [];

  for (const keyword of WARNING_KEYWORDS) {
    if (upperText.includes(keyword)) {
      detected.push(keyword);
    }
  }

  return detected;
}

/**
 * Extract dashboard information from an image using OCR
 * @param imageFile - The image file to process
 * @returns OCR results with odometer candidates and warnings
 */
export async function extractDashboardInfo(
  imageFile: File | Blob
): Promise<DashboardOcrResult> {
  const startTime = Date.now();

  try {
    // Run Tesseract OCR
    const result = await Tesseract.recognize(imageFile, 'eng', {
      logger: (m) => {
        // Optional: log progress
        if (m.status === 'recognizing text') {
          console.log(`OCR progress: ${Math.round((m.progress || 0) * 100)}%`);
        }
      },
    });

    const rawText = result.data.text;
    const odometerCandidates = parseOdometerCandidates(rawText);
    const warningsDetected = detectWarnings(rawText);

    return {
      raw_text: rawText,
      odometer_candidates: odometerCandidates,
      selected_odometer: odometerCandidates[0], // Best guess is first candidate
      warnings_detected: warningsDetected,
      processing_time_ms: Date.now() - startTime,
    };
  } catch (error) {
    console.error('OCR extraction failed:', error);
    return {
      raw_text: '',
      odometer_candidates: [],
      selected_odometer: undefined,
      warnings_detected: [],
      processing_time_ms: Date.now() - startTime,
    };
  }
}

/**
 * Validate an odometer reading is reasonable
 */
export function isValidOdometer(value: number | undefined): boolean {
  if (value === undefined || value === null) return false;
  return value >= 0 && value <= 999999;
}
